import { useState } from 'react'

import { Link, Image, Text } from '@/ui'
import { PayrollInterestScreenName } from './PayrollInterestFlow'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectIsCurrentUserScorp,
  selectIsGroupPractice,
} from '@/selectors/user.selectors'
import { LearnMorePayrollModal } from '../LearnMorePayrollModal'
import { TypographyStyles } from '@/styles'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import { ProductFeatureId } from '@/actions/permissionActions'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import { PayrollVariant } from '../payrollVariant.constants'

const INTRO_HEADINGS: Record<PayrollVariant, { title: string; lede: string }> =
  {
    [PayrollVariant.scorp]: {
      title: 'Payroll for S corporations',
      lede: 'As an S corporation, you’re required by the IRS to pay yourself and any employees through payroll.',
    },
    [PayrollVariant.group]: {
      title: 'Payroll for group practice owners',
      lede: 'As a group practice, you’re required by the IRS to pay employees through payroll.',
    },
    [PayrollVariant.general]: {
      title: 'Payroll for your practice',
      lede: 'Heard Payroll pays you and any employees, and files the payroll taxes that come with it.',
    },
  }

export const PayrollInterestIntro = ({
  setScreen,
}: {
  setScreen: (screen: string | null) => void
}) => {
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const isGroupPractice = useReselector(selectIsGroupPractice)
  const pickVariant = (): PayrollVariant => {
    if (isScorp) return PayrollVariant.scorp
    if (isGroupPractice) return PayrollVariant.group
    return PayrollVariant.general
  }
  const variant = pickVariant()
  const hasFreePayrollWithSubscription = useReselector(
    selectUserHasPermission,
    ProductFeatureId.payrollIncludedForFreeForSelf
  )
  const [showPayrollModal, setShowPayrollModal] = useState(false)

  return (
    <div className="flex flex-col gap-6 max-w-[80%] mx-auto">
      <Text variant={TypographyStyles.H1}>{INTRO_HEADINGS[variant].title}</Text>

      <Text variant={TypographyStyles.BODY_MD}>
        {INTRO_HEADINGS[variant].lede}
      </Text>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="w-[80px] flex-shrink-0 flex items-center justify-center">
          <Image
            alt="Bank"
            src="https://heard-images.s3.amazonaws.com/assets/Bank.svg"
            className="w-20 h-20"
          />
        </div>
        <div className="flex-1">
          {variant === PayrollVariant.scorp && (
            <>
              <Text variant={TypographyStyles.H3}>
                Why am I required to have payroll?
              </Text>
              <br />
              <Text variant={TypographyStyles.BODY_MD}>
                This guarantees that payroll taxes (Medicare and Social
                Security) are being collected on the{' '}
                <Link
                  href="https://support.joinheard.com/hc/en-us/articles/7475592736919-How-do-I-set-a-reasonable-salary-and-why-is-it-important"
                  newPage
                >
                  reasonable salary
                </Link>{' '}
                that you must pay yourself.
              </Text>
            </>
          )}
          {variant === PayrollVariant.group && (
            <>
              <Text variant={TypographyStyles.H3}>
                Do I need to have payroll?
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                Group practice owners generally need to manage payroll if they
                have any employees on staff as W-2 workers, which means they are
                required to withhold taxes and pay their employees a salary or
                hourly wage.
                <br />
                <br />
                If you only utilize independent contractors (1099s), you may not
                need to handle payroll for those individuals as they are
                responsible for their own taxes.
              </Text>
            </>
          )}
          {variant === PayrollVariant.general && (
            <>
              <Text variant={TypographyStyles.H3}>
                Do I need to have payroll?
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                You need payroll if you pay anyone a salary or hourly wage as a
                W-2 employee, including yourself once your practice is taxed as
                an S corporation.
                <br />
                <br />
                If you only work with independent contractors (1099s), they are
                responsible for their own taxes and you may not need payroll for
                them.
              </Text>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="w-[80px] flex-shrink-0 flex items-center justify-center">
          <Image
            alt="Question Coin"
            src="https://heard-images.s3.amazonaws.com/assets/question_coin.svg"
            className="w-20 h-20"
          />
        </div>
        <div className="flex-1">
          <Text variant={TypographyStyles.H3}>
            What if I don’t have a payroll provider?
          </Text>
          <br />
          <Text variant={TypographyStyles.BODY_MD}>
            You can sign up for{' '}
            <Link onClick={() => setShowPayrollModal(true)} href="#">
              Heard Payroll
            </Link>
            {hasFreePayrollWithSubscription
              ? ' in a later step. This service is included in your Heard membership.'
              : ' as an add-on service. You’ll be able to do this in a later step.'}
          </Text>
        </div>
      </div>

      <Text variant={TypographyStyles.BODY_MD}>
        Next, we’ll ask for some details about your payroll needs.
      </Text>

      <FormFlowFooter
        onBack={() => setScreen(null)}
        onForward={() => setScreen(PayrollInterestScreenName.FORM)}
      />

      <LearnMorePayrollModal
        open={showPayrollModal}
        onClose={() => setShowPayrollModal(false)}
      />
    </div>
  )
}
